import React from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Paper,
  CircularProgress,
} from "@mui/material";
import { Person, Business } from "@mui/icons-material";
import { formatNumber } from "../Utils";

/**
 * Shared content component for displaying incoming call information
 * Used in both IncomingDialog (telephone tab) and Webphone widget
 *
 * @param {Object} props - Component props
 * @param {Object} props.crmData - CRM data for the contact
 * @param {Object} props.incomingData - Incoming call data
 * @param {Object} props.ctiData - CTI data (queue, client code, etc.)
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message if any
 * @param {Object} props.colorScheme - Color scheme for styling
 * @param {boolean} props.showError - Whether to show error messages
 * @param {boolean} props.showAvatar - Whether to show avatar
 * @param {Function} props.getDisplayName - Function to get display name
 * @param {Function} props.getInitials - Function to get initials from name
 */
const IncomingCallContent = ({
  crmData,
  incomingData,
  ctiData,
  loading = false,
  error = null,
  colorScheme = {
    primary: '#667eea',
    secondary: '#3766be',
    text: 'white',
    chipBg: 'rgba(255, 255, 255, 0.2)',
    chipText: 'white'
  },
  showError = true,
  showAvatar = true,
  getDisplayName,
  getInitials,
}) => {
  return (
    <Box sx={{ py: 2 }}>
      {/* Error Message */}
      {showError && error && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            backgroundColor: "rgba(255, 255, 255, 0.15)",
            borderRadius: 1
          }}
        >
          <Typography variant="body2" sx={{ opacity: 0.9, color: colorScheme.text }}>
            ⚠️ {error}
          </Typography>
        </Box>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <CircularProgress sx={{ color: colorScheme.text, mb: 2 }} />
          <Typography variant="body2" sx={{ opacity: 0.8, color: colorScheme.text }}>
            Carregando informações...
          </Typography>
        </Box>
      ) : (
        <>
          {/* Contact Avatar */}
          {showAvatar && (
            <Avatar
              className="incoming-call-pulse contact-avatar"
              sx={{
                width: 100,
                height: 100,
                mx: "auto",
                mb: 3,
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                fontSize: "2rem",
                fontWeight: "bold",
                border: "3px solid rgba(255, 255, 255, 0.3)",
              }}
            >
              {crmData?.name ? (
                getInitials(crmData.name)
              ) : (
                <Person sx={{ fontSize: 50 }} />
              )}
            </Avatar>
          )}

          {/* Contact Name */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              mb: 1,
              textShadow: "0 2px 4px rgba(0,0,0,0.3)",
              color: colorScheme.text,
              textAlign: "center",
            }}
          >
            {getDisplayName()}
          </Typography>

          {/* Phone Number */}
          {incomingData?.number && (
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                mb: 2,
                fontFamily: "monospace",
                letterSpacing: 1,
                color: colorScheme.text,
                textAlign: "center",
              }}
            >
              {formatNumber(incomingData, true)}
            </Typography>
          )}

          {/* Contact Information Section */}
          {(crmData?.contact_enterprise_name || ctiData) && (
            <Paper
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                p: 2,
                mb: 2,
                borderRadius: 2,
                backdropFilter: "blur(10px)",
              }}
            >
              {/* Enterprise Name */}
              {crmData?.contact_enterprise_name && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1
                  }}
                >
                  <Business sx={{ mr: 1, fontSize: 20, color: colorScheme.text }} />
                  <Typography variant="body1" sx={{ fontWeight: 500, color: colorScheme.text }}>
                    {crmData.contact_enterprise_name}
                  </Typography>
                </Box>
              )}

              {/* CTI Information as Chips */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
                {ctiData?.queue && (
                  <Chip
                    label={`Fila: ${ctiData.queue}`}
                    size="small"
                    sx={{
                      backgroundColor: colorScheme.chipBg,
                      color: colorScheme.chipText,
                      fontSize: "0.75rem",
                    }}
                  />
                )}

                {ctiData?.clientCode?.trim() && (
                  <Chip
                    label={`Cliente: ${ctiData.clientCode}`}
                    size="small"
                    sx={{
                      backgroundColor: colorScheme.chipBg,
                      color: colorScheme.chipText,
                      fontSize: "0.75rem",
                    }}
                  />
                )}

                {ctiData?.cti?.length > 0 && (
                  <Chip
                    label={`CTI: ${ctiData.cti}`}
                    size="small"
                    sx={{
                      backgroundColor: colorScheme.chipBg,
                      color: colorScheme.chipText,
                      fontSize: "0.75rem",
                    }}
                  />
                )}
              </Box>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default IncomingCallContent;
